import React from 'react';
import { Plus, Clock, Edit2, Trash2, ChevronDown, ChevronRight, GripVertical, Music } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlanElement } from '@/services/api';
import { cn } from '@/lib/utils';
import { isPlanRadSortable } from '@/lib/planDndUtils';
import { SortableHendelseRow } from './SortableHendelseRow';

/**
 * Sortable row component for overskrift (header) elements
 * 
 * Displays a draggable overskrift with:
 * - Grip handle for drag-and-drop
 * - Expand/collapse button
 * - Header name
 * - "Før start" badge if applicable
 * - Count of child hendelser
 * - Edit and delete buttons
 * - Collapsible list of child hendelser (when expanded)
 * - "Add hendelse" button (when expanded)
 * 
 * Extracted from PlanEditor.tsx for better modularity.
 */

interface SortableOverskriftRowProps {
  overskrift: PlanElement;
  hendelser: PlanElement[];
  isExpanded: boolean;
  activeElement: PlanElement | null;
  onToggleHeader: (id: number) => void;
  onEdit: (element: PlanElement) => void;
  onDelete: (id: number) => void;
  onOpenHendelseDialog: (parentId: number) => void;
  onOpenSangDialog: (parentId: number) => void;
  onSelectSong?: (placeholder: PlanElement) => void;
  calculateStartTime: (id: number) => Date | null;
  formatTime: (date: Date) => string;
  formatDurationShort: (minutter: number) => string;
  readOnly?: boolean;
}

export const SortableOverskriftRow: React.FC<SortableOverskriftRowProps> = React.memo(({
  overskrift,
  hendelser,
  isExpanded,
  activeElement,
  onToggleHeader,
  onEdit,
  onDelete,
  onOpenHendelseDialog,
  onOpenSangDialog,
  onSelectSong,
  calculateStartTime,
  formatTime,
  formatDurationShort,
  readOnly = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: overskrift.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const showDropZone =
    isOver &&
    activeElement != null &&
    isPlanRadSortable(activeElement) &&
    activeElement.parent_id !== overskrift.id;

  return (
    <div className="relative">
      {isOver && activeElement?.type === 'overskrift' && activeElement.id !== overskrift.id && (
        <div className="absolute -top-1 left-0 right-0 h-1 bg-primary z-50 rounded-full shadow-lg">
          <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 w-4 h-4 bg-primary rounded-full border-2 border-white shadow-lg" />
        </div>
      )}
      
      <div ref={setNodeRef} style={style}>
        <div
          className={cn(
            'bg-primary text-primary-foreground transition-all',
            isDragging ? 'opacity-0' : '',
            showDropZone ? 'ring-4 ring-green-400 ring-inset' : ''
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3">
            {!readOnly && (
              <div 
                {...attributes} 
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-primary-foreground/70 hover:text-primary-foreground flex items-center shrink-0"
              >
                <GripVertical className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleHeader(overskrift.id)}
              className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground hover:bg-primary-foreground/20 shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </Button>

            <h3 className="flex-1 font-semibold text-sm sm:text-base truncate min-w-0">{overskrift.navn}</h3>

            {overskrift.før_produksjonsstart && (
              <Badge 
                variant="secondary" 
                className="bg-amber-100 text-amber-800 border-amber-200 text-xs shrink-0 gap-1 hidden xs:flex"
              >
                <Clock className="h-3 w-3" />
                <span>Før start</span>
              </Badge>
            )}
            {overskrift.før_produksjonsstart && (
              <div className="xs:hidden shrink-0 flex items-center justify-center w-6 h-6 bg-amber-100 rounded-full" title="Før produksjonsstart">
                <Clock className="h-3.5 w-3.5 text-amber-600" />
              </div>
            )}

            <Badge 
              variant="secondary" 
              className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30 text-xs shrink-0"
            >
              {hendelser.length}
            </Badge>

            {!readOnly && (
              <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(overskrift);
                  }}
                  className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground hover:bg-primary-foreground/20"
                >
                  <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(overskrift.id);
                  }}
                  className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground hover:bg-primary-foreground/20"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className={cn(
          "bg-muted/30 border-l-4 border-primary transition-all",
          showDropZone ? 'bg-green-100/50 border-green-400' : ''
        )}>
          {showDropZone && (
            <div className="p-3 bg-green-100 border-y border-green-300 text-green-800 text-sm font-medium flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Slipp her for å flytte til "{overskrift.navn}"
            </div>
          )}
          
          {hendelser.length === 0 ? (
            <div className={cn(
              "p-4 text-sm text-muted-foreground italic",
              showDropZone ? 'bg-green-50 text-green-700' : ''
            )}>
              {showDropZone ? 'Slipp her for å legge til som første innslag' : 'Ingen innslag ennå'}
            </div>
          ) : (
            <SortableContext
              items={hendelser.map(h => h.id)}
              strategy={verticalListSortingStrategy}
            >
              {hendelser.map((hendelse) => (
                <SortableHendelseRow 
                  key={hendelse.id} 
                  hendelse={hendelse}
                  activeElement={activeElement}
                  calculateStartTime={calculateStartTime}
                  formatTime={formatTime}
                  formatDurationShort={formatDurationShort}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onSelectSong={onSelectSong}
                  readOnly={readOnly}
                />
              ))}
            </SortableContext>
          )}
          
          {!readOnly && (
            <div className="p-2 sm:p-3 border-t border-border/50 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => onOpenHendelseDialog(overskrift.id)}
                variant="outline"
                size="sm"
                className="gap-2 text-xs sm:text-sm w-full sm:w-auto"
              >
                <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                Legg til innslag
              </Button>
              <Button
                onClick={() => onOpenSangDialog(overskrift.id)}
                variant="outline"
                size="sm"
                className="gap-2 text-xs sm:text-sm w-full sm:w-auto"
              >
                <Music className="h-3 w-3 sm:h-4 sm:w-4" />
                Legg til sang
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

SortableOverskriftRow.displayName = 'SortableOverskriftRow';

